import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from "react-router";
import { ApplicationState } from '../../../store';
import { SurveyResponseState } from '../../../store/StateControllers/Survey/SurveyResponse/SurveyResponseStates';
import { ToxnotComponentProperties } from '../BaseApplicationComponents/ToxnotComponent';
import { ToxnotSignInRequiredComponent } from '../BaseApplicationComponents/ToxnotSignInRequiredComponent';
import * as SurveyResponseController from "../../../store/StateControllers/Survey/SurveyResponse/SurveyResponseController";
import { QuestionnairePage, ResponseBomType, SurveyPageType } from '../../../store/Models/Surveys/SurveyDataModel';
import { TeamRole } from '../../../store/Models/UserContext';
import {
    coreValues,
    surveyResponseMaterialsPerPage
} from "../../../store/StateControllers/Survey/SurveyResponse/SurveyResponseController";

interface LocalProps {
    onNavigation: () => void;
}

interface LocalState {
    rerender: boolean;
}

type SurveyResponseProperties =
    ToxnotComponentProperties &
    RouteComponentProps<{ responseId?: string }> &
    SurveyResponseState &
    typeof SurveyResponseController.actionCreators & 
    LocalProps;

class SurveyNavigation extends ToxnotSignInRequiredComponent<SurveyResponseProperties, LocalState> {
    protected getDisallowedRoles(): TeamRole[] {
        return [TeamRole.Marketing, TeamRole.Viewer];
    }

    protected renderConcreteComponent(): JSX.Element | null {
        
        const backButton = (this.props.navContext.mainNavIndex != SurveyPageType.Instructions)
            ? <button onClick={(e) => this.previousPage()} name="previous-button" className="btn btn-default navigation-btn m-r-10">{this.localizeString("Back")}</button>
            : null;

        const nextButton =
            <button name="next-button" className="btn btn-toxnot-primary navigation-btn" onClick={async (e) => await this.nextPage()}>
                {this.getNextButtonText()}
            </button>

        return (
            <div className="survey-response-action-container m-t-15">
                {backButton}
                {" "}
                {SurveyResponseController.isLastItem() && (this.hasResponseBeenSubmittedOrIsBeingSubmitted()) ? "" : nextButton}
            </div>
        )
    }

    private hasResponseBeenSubmittedOrIsBeingSubmitted = (): boolean => (
        this.props.coreData && this.props.coreData.response && (this.props.coreData.response.state === 1 || (this.props.coreData.response.responseDataJson && JSON.parse(this.props.coreData.response.responseDataJson).IsOfflineProcessStarted))
    );

    private getNextButtonText(): string {
        const currentPage = this.getCurrentPage();
        const isNextClicked = this.props.navContext.mainNavIndex === SurveyPageType.Bom ? this.isBomNextClicked() : currentPage?.nextClicked;
        const isPageValid = this.props.navContext.mainNavIndex === SurveyPageType.Bom ? this.isBomPageValid() : this.isPageValid(currentPage);
        const extensionRequested = this.props.coreData?.extensionRequest?.inventoryIds?.some(i => i === this.props.selectedInventory?.inventoryId);


        if (isNextClicked && this.props.coreData?.response.state !== 1 && ((!isPageValid && !SurveyResponseController.isLastItem()) || (!extensionRequested && this.pageNeedsWarning(currentPage)))) {
            return this.localizeString("Skip");
        }

        return this.localizeString("Next");
    }

    private isBomPageValid() {
        if (this.props.selectedInventory === undefined || this.props.selectedInventory === null) {
            return false;
        }

        const bomValidation = this.props.validationResult.bomStatusTable[this.props.selectedInventory?.inventoryId]?.inventoryBomValidation;

        const isInvalid = (bomValidation !== null && bomValidation !== undefined && bomValidation.length > 0) ||
                          (bomValidation !== null && bomValidation !== undefined && bomValidation.length === 0 && !this.props.validationResult.bomStatusTable[this.props.selectedInventory?.inventoryId]?.fieldsStatus);

        return !isInvalid;
    }

    private isBomNextClicked() {
        return this.props.selectedInventory?.bomNextClicked;
    }

    private pageNeedsWarning(currentPage: QuestionnairePage | undefined): boolean {
        if (currentPage === undefined || currentPage?.questions.every(x=> x.hidden)) {
            return false; // bom page does not have warnings
        }

        return !currentPage?.questions.some(q => !SurveyResponseController.doesQuestionNeedValueAndNotHaveValue(q));
    }

    private async nextPage() {
        const currentPage = this.getCurrentPage();
        const extensionRequested = this.props?.coreData?.extensionRequest?.inventoryIds.some(i => i === this.props.selectedInventory?.inventoryId);
        let paginationDetails = coreValues(this.props.coreData);

        if (this.props.coreData?.response.state !== 1 &&
            SurveyResponseController.isOverallValid(this.props.validationResult) &&
            //Current Page equals to Last page [currentPageOfMaterialsIndex starts with index 0]
            ((this.props.navContext.currentPageOfMaterialsIndex + 1) === paginationDetails.totalPages) &&
            (extensionRequested || !this.pageNeedsWarning(currentPage) || currentPage?.nextClicked) &&
            SurveyResponseController.isLastItem() && (currentPage !== undefined && currentPage !== null || this.props.navContext.mainNavIndex === SurveyPageType.Bom || this.props.navContext.mainNavIndex === SurveyPageType.Inventory)) {
            return this.showModal("submit-survey-response-modal");
        }

        switch (this.props.navContext.mainNavIndex) {
            case SurveyPageType.Instructions:
                this.goToNextPageForInstructionsMainPage();
                return;
            case SurveyPageType.SupplierQuestionnaire:
                if (currentPage !== undefined && currentPage !== null && !currentPage.nextClicked) {
                    this.handleNextClickedWithCb(this.props.setCurrentSupplierPageNextClicked, currentPage.title);

                    if (!this.isPageValid(currentPage) || (!extensionRequested && this.pageNeedsWarning(currentPage))) {
                        return;
                    }
                }

                this.goToNextPageForSupplierMainPage();
                return;
            case SurveyPageType.AddMaterials:
                this.goToNextPageForAddMaterialsMainPage();
                return;
            case SurveyPageType.GroupInventories:
                this.goNextFromGroups();
                return;
            case SurveyPageType.Batch:
                this.setSurveySelectedInventoryIfAny(0);
                if(this.props.navContext.currentPageOfMaterialsIndex > 0){
                    this.props.getPaginatedSurveyData(0, true, 0);
                }
                return;
            case SurveyPageType.Inventory:
                if (currentPage !== undefined && currentPage !== null && !currentPage.nextClicked) {
                    this.handleNextClickedWithCb(this.props.setCurrentPageNextClicked);

                    if ( this.props.coreData?.response.state !== 1 && (!this.isPageValid(currentPage) || (!extensionRequested && this.pageNeedsWarning(currentPage)))) {
                        return;
                    }
                }

                this.goToNextPageForInventory();
                return;
            case SurveyPageType.Bom:
                if (this.props.selectedInventory !== undefined && this.props.selectedInventory !== null) {
                    if (!this.props.selectedInventory.bomNextClicked) {
                        this.handleNextClickedWithCb(this.props.setBomPageNextClicked);

                        if (!this.isBomPageValid()) {
                            return;
                        }
                    }
                }

                await this.updateInventoryAfterBomUpdates();
                this.goNextFromBom();
                return;
            default:
                return;
        }
    }

    private handleNextClickedWithCb = (cb: (cbParam?: string) => void, cbParam?: string) => {
        if (cbParam !== undefined) {
            cb.call(this, cbParam);
        } else {
            cb.call(this);
        }

        if (this.props.onNavigation !== null && this.props.onNavigation !== undefined) {
            this.props.onNavigation();
        }

        this.setState({ rerender: !this.state?.rerender });
    }

    private async previousPage() {
        switch (this.props.navContext.mainNavIndex) {
            case SurveyPageType.Inventory:
                this.goBackForInventory();
                return;
            case SurveyPageType.Bom:
                this.props.setNavigationContext(SurveyPageType.Inventory, this.props.navContext.inventoryNavIndex, -1, -1);
                return;
            case SurveyPageType.GroupInventories:
                this.goBackForGroup();
                return;
            case SurveyPageType.Batch:
                this.goBackForBatch();
                return;
            case SurveyPageType.SupplierQuestionnaire:
                this.goBackForSupplierQuestionnaire();
                return;
            case SurveyPageType.AddMaterials:
                this.goBackForAddMaterials();
                return;

            default:
                return;
        }
    }

    private isPageValid(currentPage: QuestionnairePage | undefined): boolean {
        let status = false;

        if (this.props.navContext.mainNavIndex === SurveyPageType.SupplierQuestionnaire) {
            if (currentPage === undefined) {
                return status;
            }

            return this.props.validationResult.supplierQuestionnaireStatusList.firstOrDefault(p => p.page === currentPage.title || p.page === currentPage.type)?.pageIsComplete ?? status;
        } else if (this.props.navContext.mainNavIndex === SurveyPageType.Inventory) {
            return this.isInventoryPageValid(currentPage);
        } else if (this.props.navContext.mainNavIndex === SurveyPageType.Bom) {
            return this.isBomPageValid();
        }

        return status;
    }

    private isInventoryPageValid(currentPage: QuestionnairePage | undefined) {
        let status = false;

        if (this.props.selectedInventory !== null && this.props.selectedInventory !== undefined && currentPage !== undefined && currentPage !== null) {
            const inventory = this.props.validationResult.bomStatusTable[this.props.selectedInventory?.inventoryId];
            const currentQuestionnaireType = this.props.selectedInventory?.questionnairesTypes[this.props.navContext.surveyQuestionnaireIndex]
            const page = SurveyResponseController.getCurrentPageForValidation(inventory, currentQuestionnaireType, currentPage.type, currentPage.title);

            return !page?.questions.some(q => !q.valid);
        }

        return status;
    }

    private getCurrentPage(): QuestionnairePage | undefined {
        if (this.props.navContext.mainNavIndex === SurveyPageType.SupplierQuestionnaire) {
            return this.getCurrentSupplierQuestionnairePage();
        } else if (this.props.navContext.mainNavIndex === SurveyPageType.Inventory) {
            return this.getCurrentInventoryPage();
        }
    }

    private getCurrentInventoryPage() {
        return this.props.surveyQuestionnairesList[this.props.navContext.surveyQuestionnaireIndex]?.pages[this.props.navContext.surveyQuestionnairePageIndex];
    }

    private getCurrentSupplierQuestionnairePage() {
        const supplierQuestionnaire = SurveyResponseController.getSupplierLevelQuestionnaire();
        return supplierQuestionnaire?.questionnaire.pages[this.props.navContext.surveyQuestionnairePageIndex];
    }

    private setSurveySelectedInventoryIfAny(inventoryIndex: number, surveyQuestionnaireIndex?: number, surveyQuestionnairePageIndex?: number, mainNavIndex?: number) {
        this.props.setSurveySelectedInventory(inventoryIndex, surveyQuestionnaireIndex, surveyQuestionnairePageIndex, mainNavIndex);
    }

    private goToNextPageForInstructionsMainPage() {
        if (this.props.coreData!.response.supplierQuestionnaireData !== null && (!this.props.coreData?.response.isRevisionResponse || this.props.coreData!.response.supplierQuestionnaireData.revisionRequest != null)) {
            this.props.setMainNavPage(SurveyPageType.SupplierQuestionnaire, -1, 0);
        } else if (this.props.coreData!.response.bomType === ResponseBomType.Open) {
            this.props.setMainNavPage(SurveyPageType.AddMaterials, -1, 0);
        } else {
            this.goToGroupsOrProducts();
        }
    }

    private goToNextPageForAddMaterialsMainPage() {
        this.goToGroupsOrProducts();
    }

    private goToNextPageForSupplierMainPage() {
        const nextPageJump = this.props.coreData?.response?.supplierQuestionnaireData?.questionnaire?.pages[this.props.navContext.surveyQuestionnairePageIndex]?.defaultNextPageIndex;

        //keep track of the jump to statement for the back action
        let previousPageStack = [...this.props.navContext.previousPageStack];
        previousPageStack.push(this.props.navContext.surveyQuestionnairePageIndex);

        if (this.props.coreData!.response.supplierQuestionnaireData !== null) {
            if (this.props.navContext.surveyQuestionnairePageIndex < this.props.coreData!.response.supplierQuestionnaireData.questionnaire.pages.length - 1) {
                //if the page has a jump to (defaultNextPageIndex) ignore the default behavior and and jump to the next specified page.
                if (nextPageJump) {
                    this.props.setMainNavPage(SurveyPageType.SupplierQuestionnaire, this.props.navContext.surveyQuestionnaireIndex, nextPageJump, previousPageStack);
                } else {
                    this.props.setMainNavPage(SurveyPageType.SupplierQuestionnaire, this.props.navContext.surveyQuestionnaireIndex, this.props.navContext.surveyQuestionnairePageIndex + 1, previousPageStack);
                }
            } else if (this.props.coreData!.response.bomType === ResponseBomType.Open) {
                this.props.setMainNavPage(SurveyPageType.AddMaterials, -1, 0);
            } else {
                this.goToGroupsOrProducts();
            }
        }
    }

    private goToGroupsOrProducts() {
        const mainInventoryList = SurveyResponseController.getMainSupplierFacingMaterialInventoryList();
        const originalRequestInventoryList = SurveyResponseController.getOriginalRequestMaterialInventoryList();
        const paginationDetails = coreValues(this.props.coreData); 
        const totalInventories = paginationDetails.totalInventories;

        if ((originalRequestInventoryList.length > 0) && (totalInventories > 1) && (mainInventoryList.length >= SurveyResponseController.materiaListSizeGroupThreshold || this.props.coreData?.response?.groupedResponseInventories && Object.values(this.props.coreData?.response?.groupedResponseInventories).length > 0)) {
            this.props.setMainNavPage(SurveyPageType.GroupInventories);
        } else if (mainInventoryList.length > 0){
            this.setSurveySelectedInventoryIfAny(0);
        }
    }

    private goNextFromGroups() {
        const mainInventoryList = SurveyResponseController.getMainSupplierFacingMaterialInventoryList();

        if (mainInventoryList.length >= SurveyResponseController.materalListSizeBatchAnswerThreshold) {
            this.goToBatch();
        } else if (mainInventoryList.length > 0) {
            this.setSurveySelectedInventoryIfAny(0);
        }
    }

    private goToBatch() {
        this.props.setMainNavPage(SurveyPageType.Batch);
    }

    private goToNextPageForInventory() {
        if (this.props.navContext.surveyQuestionnairePageIndex == -1) {
            if (this.props.selectedInventory?.hasBomPage) {
                this.props.setNavigationContext(SurveyPageType.Bom, this.props.navContext.inventoryNavIndex, -1, -1);
            } else if (this.props.selectedInventory?.questionnaireList !== undefined && this.props.selectedInventory?.questionnaireList?.length > 0) {
                this.props.setNavigationContext(this.props.navContext.mainNavIndex, this.props.navContext.inventoryNavIndex, 0, 0, this.props.navContext.previousPageStack);
            } else if (this.props.navContext.surveyQuestionnaireIndex == -1) {
                this.setSurveySelectedInventoryIfAny(this.props.navContext.inventoryNavIndex + 1);
            }
        } else {
            this.goToNextQuestionnaireOrPage();
        }
    }

    private goToNextQuestionnaireOrPage() {
        const mainInventoryList = SurveyResponseController.getMainSupplierFacingMaterialInventoryList();
        const nextPageJump = this.props.surveyQuestionnairesList[this.props.navContext.surveyQuestionnaireIndex]?.pages[this.props.navContext.surveyQuestionnairePageIndex]?.defaultNextPageIndex;

        //keep track of the jump to statement for the back action
        let previousPageStack = [...this.props.navContext.previousPageStack];
        previousPageStack.push(this.props.navContext.surveyQuestionnairePageIndex);

        if (this.props.navContext.surveyQuestionnairePageIndex < this.props.surveyQuestionnairesList[this.props.navContext.surveyQuestionnaireIndex]?.pages?.length - 1) {
            //if the page has a jump to (defaultNextPageIndex) ignore the default behavior and jump to the next specified page.
            if (nextPageJump) {
                this.props.setNavigationContext(this.props.navContext.mainNavIndex, this.props.navContext.inventoryNavIndex, this.props.navContext.surveyQuestionnaireIndex, nextPageJump, previousPageStack);
            } else {
                this.props.setNavigationContext(this.props.navContext.mainNavIndex, this.props.navContext.inventoryNavIndex, this.props.navContext.surveyQuestionnaireIndex, this.props.navContext.surveyQuestionnairePageIndex + 1, previousPageStack);
            }
        } else if (this.props.navContext.surveyQuestionnaireIndex < this.props.surveyQuestionnairesList.length - 1) {
            //if the page has a jump to (defaultNextPageIndex) ignore the default behavior and jump to the next specified page.
            if (nextPageJump) {
                this.props.setNavigationContext(this.props.navContext.mainNavIndex, this.props.navContext.inventoryNavIndex, nextPageJump, 0, previousPageStack);
            } else {
                this.props.setNavigationContext(this.props.navContext.mainNavIndex, this.props.navContext.inventoryNavIndex, this.props.navContext.surveyQuestionnaireIndex + 1, 0, []);
            }
        } else if ((this.props.navContext.inventoryNavIndex + 1) < mainInventoryList.length) {
            if (this.props.navContext.selectedInventoryPageIndex === this.props.navContext.currentPageOfMaterialsIndex) {
                this.setSurveySelectedInventoryIfAny(this.props.navContext.inventoryNavIndex + 1);
            }
            else {
                //If the selected inventory's page number differs from the page user is currently on, then by clicking on the next button 
                //it should navigate user to the next inventory of the selected inventory's page.
                this.props.getPaginatedSurveyData(this.props.navContext.selectedInventoryPageIndex, true, this.props.navContext.inventoryNavIndex + 1);
            }
        } else {
            let paginationDetails = coreValues(this.props.coreData);
            //If user has selected last inventory of the page and clicks next And
            //If user is already on the next page in navigation side menu then directly show the first inventory of that page.
            if (this.props.navContext.selectedInventoryPageIndex + 1 === this.props.navContext.currentPageOfMaterialsIndex) {
                this.setSurveySelectedInventoryIfAny(0);
            }
            //if the user is on last material of last page then there is no next page to load and no navigation operation should be performed
            else if((this.props.navContext.currentPageOfMaterialsIndex + 1) !== paginationDetails.totalPages ){
                //If user is on the last inventory of the page and clicks next button, it should navigate user to the first inventory of the new page
                this.props.getPaginatedSurveyData(this.props.navContext.selectedInventoryPageIndex + 1, true, 0);
            }
        }
    }

    private goNextFromBom() {
        if (this.props.surveyQuestionnairesList[0]) {
            this.props.setNavigationContext(SurveyPageType.Inventory, this.props.navContext.inventoryNavIndex, 0, 0);
        } else {
            const mainInventoryList = SurveyResponseController.getMainSupplierFacingMaterialInventoryList();

            if (mainInventoryList.length - 1 > this.props.navContext.inventoryNavIndex) {
                this.setSurveySelectedInventoryIfAny(this.props.navContext.inventoryNavIndex + 1);
            }
        }
    }
    
    private async updateInventoryAfterBomUpdates() {
        this.props.dispatchLoadingState(true);
        let result = await SurveyResponseController.getInventoryDetails(this.props.responseId, this.props.selectedInventory?.inventoryId || "");
        this.props.dispatchUpdateInventoryDetails(result, this.props.selectedInventory?.inventoryId || '');
    }
    
    private goBackForGroup() {
        if (this.props.coreData!.response.bomType === ResponseBomType.Open) {
            this.props.setMainNavPage(SurveyPageType.AddMaterials);
        } else if (this.props.coreData!.response.supplierQuestionnaireData !== null &&
            (!this.props.coreData?.response.isRevisionResponse || this.props.coreData!.response.supplierQuestionnaireData.revisionRequest != null)) {
            this.props.setMainNavPage(SurveyPageType.SupplierQuestionnaire, -1, this.props.coreData!.response.supplierQuestionnaireData.questionnaire.pages.length - 1);
        } else {
            this.props.setMainNavPage(SurveyPageType.Instructions);
        }
    }

    private goBackForBatch() {
        this.props.setMainNavPage(SurveyPageType.GroupInventories);
    }

    private goBackForAddMaterials() {
        if (this.props.coreData!.response.supplierQuestionnaireData !== null &&
            (!this.props.coreData?.response.isRevisionResponse || this.props.coreData!.response.supplierQuestionnaireData.revisionRequest != null)) {
            this.props.setMainNavPage(SurveyPageType.SupplierQuestionnaire, -1, this.props.coreData!.response.supplierQuestionnaireData.questionnaire.pages.length - 1);
        } else {
            this.props.setMainNavPage(SurveyPageType.Instructions);
        }
    }

    private goBackForSupplierQuestionnaire() {
        if (this.props.navContext.previousPageStack.length > 0) {
            let stack = [...this.props.navContext.previousPageStack];
            let prevPage = stack.pop();

            this.props.setMainNavPage(SurveyPageType.SupplierQuestionnaire, -1, prevPage, stack);
        } else if (this.props.navContext.surveyQuestionnairePageIndex > 0 && this.props.coreData!.response.supplierQuestionnaireData && this.props.coreData!.response.supplierQuestionnaireData?.questionnaire.pages.length > 1) {
            this.props.setMainNavPage(SurveyPageType.SupplierQuestionnaire, -1, this.props.navContext.surveyQuestionnairePageIndex - 1);
        } else {
            this.props.setMainNavPage(SurveyPageType.Instructions);
        }
    }

    private goBackToBomOrProductDetails() {
        if (this.props.selectedInventory?.hasBomPage) {
            this.props.setNavigationContext(SurveyPageType.Bom, this.props.navContext.inventoryNavIndex, -1, -1);
        } else {
            this.props.setNavigationContext(this.props.navContext.mainNavIndex, this.props.navContext.inventoryNavIndex, -1, -1);
        }
    }

    private goBackFromProductDetails() {
        const mainInventoryList = SurveyResponseController.getMainSupplierFacingMaterialInventoryList();
        const originalRequestInventoryList = SurveyResponseController.getOriginalRequestMaterialInventoryList();
        const paginationDetails = coreValues(this.props.coreData); 
        const totalInventories = paginationDetails.totalInventories;

        if (mainInventoryList.length >= SurveyResponseController.materiaListSizeGroupThreshold && (totalInventories > 1)) {
            this.props.setMainNavPage(SurveyPageType.Batch);
        } else if ((originalRequestInventoryList.length > 0) && (totalInventories > 1) && (mainInventoryList.length >= SurveyResponseController.materiaListSizeGroupThreshold || this.props.coreData?.response?.groupedResponseInventories && Object.values(this.props.coreData?.response?.groupedResponseInventories).length > 0)) {
            this.props.setMainNavPage(SurveyPageType.GroupInventories);
        } else if (this.props.coreData!.response.bomType === ResponseBomType.Open) {
            this.props.setMainNavPage(SurveyPageType.AddMaterials);
        } else {
            this.goBackForAddMaterials();
        }
    }

    private goBackForInventory() {
        if (this.props.navContext.previousPageStack.length > 0) {
            let stack = [...this.props.navContext.previousPageStack];
            let prevPage = stack.pop();

            this.props.setNavigationContext(this.props.navContext.mainNavIndex, this.props.navContext.inventoryNavIndex, this.props.navContext.surveyQuestionnaireIndex, prevPage ?? 0, stack);
        } else if (this.props.navContext.surveyQuestionnairePageIndex > 0) {
            this.props.setNavigationContext(this.props.navContext.mainNavIndex, this.props.navContext.inventoryNavIndex, this.props.navContext.surveyQuestionnaireIndex, this.props.navContext.surveyQuestionnairePageIndex - 1, this.props.navContext.previousPageStack);
        } else if (this.props.navContext.surveyQuestionnaireIndex == 0) {
            this.goBackToBomOrProductDetails();
        } else if (this.props.surveyQuestionnairesList.length > 1 && this.props.navContext.surveyQuestionnaireIndex > 0) {
            this.props.setNavigationContext(this.props.navContext.mainNavIndex, this.props.navContext.inventoryNavIndex, this.props.navContext.surveyQuestionnaireIndex - 1, this.props.surveyQuestionnairesList[this.props.navContext.surveyQuestionnaireIndex - 1].pages.length - 1, this.props.navContext.previousPageStack);
        } else if (this.props.navContext.inventoryNavIndex > 0) {
            if (this.props.surveyQuestionnairesList?.length > 0) {
                this.setSurveySelectedInventoryIfAny(this.props.navContext.inventoryNavIndex - 1, this.props.surveyQuestionnairesList.length - 1, this.props.surveyQuestionnairesList[this.props.surveyQuestionnairesList.length - 1].pages.length - 1);
            } else {
                if (this.props.selectedInventory?.hasBomPage) {
                    this.setSurveySelectedInventoryIfAny(this.props.navContext.inventoryNavIndex - 1, -1, -1, SurveyPageType.Bom);
                } else {
                    this.setSurveySelectedInventoryIfAny(this.props.navContext.inventoryNavIndex - 1);
                }
            }
        } else if (this.props.navContext.inventoryNavIndex === 0 && this.props.navContext.selectedInventoryPageIndex > 0) {
            //If user is one first inventory of the second or third page and clicks back button so he should be navigated to the last inventory of the previous page.
            if (this.props.surveyQuestionnairesList?.length > 0) {
                this.props.getPaginatedSurveyData(this.props.navContext.selectedInventoryPageIndex - 1, true, surveyResponseMaterialsPerPage - 1, this.props.surveyQuestionnairesList.length - 1, this.props.surveyQuestionnairesList[this.props.surveyQuestionnairesList.length - 1].pages.length - 1);
            } else {
                if (this.props.selectedInventory?.hasBomPage) {
                    this.props.getPaginatedSurveyData(this.props.navContext.selectedInventoryPageIndex - 1, true, surveyResponseMaterialsPerPage - 1, - 1, - 1, SurveyPageType.Bom);
                } else {
                    this.props.getPaginatedSurveyData(this.props.navContext.selectedInventoryPageIndex - 1, true, surveyResponseMaterialsPerPage - 1);
                }
            }
            //this.props.getPaginatedSurveyData(this.props.navContext.selectedInventoryPageIndex - 1, true, surveyResponseMaterialsPerPage - 1, this.props.surveyQuestionnairesList.length - 1, this.props.surveyQuestionnairesList[this.props.surveyQuestionnairesList.length - 1].pages.length - 1);
        } else {
            this.goBackFromProductDetails();
        }
    }
}

// ===============================================================================

export default connect(
    (state: ApplicationState) => ({ ...state.surveyResponseState, appServices: state.appServices }),
    SurveyResponseController.actionCreators
)(withRouter(SurveyNavigation) as any);
